import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { RedeemCashCoachMark } from '../../../features/onboarding/redeemCashCoachMark/RedeemCashCoachMark';
import { useRedeemCashCoachMark } from '../../../features/onboarding/redeemCashCoachMark/useRedeemCashCoachMark';
import { useStores } from '../../../stores/useStores';
import { BingoNotificationDot } from '../../Notifications/BingoNotificationDot';
import { NotificationDotController } from '../../Notifications/NotificationDotController';
import { useFooterNavigationLogic } from '../useFooterNavigationLogic';

import { FooterAnimations } from './FooterAnimations';
import styles from './FooterNavigation.module.scss';
import { NavigationTab } from './NavigationTab';

export const FooterNavigation = observer(function FooterNavigation() {
  const { pocStore, bingoStore, uiStore } = useStores();

  const {
    redeemModeAvailable,
    handleOpenFullPageModal,
    handleRewardOpen,
    handleOpenStore,
    handleOpenRedeem,
    handleOpenBingo,
    disableControls,
    hideNavigationBar,
  } = useFooterNavigationLogic();

  const { t } = useTranslation();

  // Called before the `hideNavigationBar` early-return below — hooks must be unconditional — which is
  // why `enabled` folds that flag in: the hook burns a once-only "seen" flag on display, and must not
  // do so on a frame where the nav renders nothing.
  const coachMark = useRedeemCashCoachMark({
    enabled: redeemModeAvailable && !hideNavigationBar,
    onRedeem: handleOpenRedeem,
  });

  const isOnBingoPage = bingoStore.isBingoPageOpen;

  if (hideNavigationBar) {
    return null;
  }

  return (
    <div
      data-footer-nav
      className={classNames(styles.footerNavigation, {
        [styles.controlsDisabled]: disableControls,
      })}
    >
      <FooterAnimations />
      <NavigationTab dialog="store" title={t('footer.store')} onClick={() => handleOpenStore()} />
      <NavigationTab
        comingSoon={!redeemModeAvailable}
        dialog="redeem"
        disabled={!redeemModeAvailable}
        setReferenceElement={coachMark.setReferenceElement}
        title={redeemModeAvailable ? t('footer.redeem') : undefined}
        onClick={() => handleOpenRedeem()}
      >
        {redeemModeAvailable ? (
          <div className={pocStore.shouldShowHangingSessionMarker ? 'hanging-poc-marker' : undefined} />
        ) : null}
      </NavigationTab>
      <NavigationTab
        dialog={'home'}
        forceInactive={isOnBingoPage}
        title={t('footer.lobby')}
        onClick={handleOpenFullPageModal}
      />
      <NavigationTab dialog="rewards" title={t('footer.lounge')} onClick={handleRewardOpen}>
        <div className={styles.notificationDotWrapper}>
          <NotificationDotController type="small" />
        </div>
      </NavigationTab>
      {bingoStore.bingoEnabled ? (
        <NavigationTab
          dialog="bingo"
          forceActive={isOnBingoPage}
          title={t('footer.bingo')}
          onClick={() => handleOpenBingo()}
        >
          {uiStore.openFullPageDialog !== 'bingo' && (
            <div className={styles.notificationDotWrapper}>
              <BingoNotificationDot type="small" />
            </div>
          )}
        </NavigationTab>
      ) : (
        <NavigationTab
          additionalDialogs={['loyalty_program']}
          dialog="account"
          title={t('footer.account')}
          onClick={handleOpenFullPageModal}
        />
      )}
      {coachMark.showCoachMark && (
        <RedeemCashCoachMark
          arrowStyles={coachMark.arrowStyles}
          popperStyles={coachMark.popperStyles}
          setArrowElement={coachMark.setArrowElement}
          setPopperElement={coachMark.setPopperElement}
          onRedeemClick={coachMark.handleRedeemClick}
        />
      )}
    </div>
  );
});
